import { useCallback, useEffect, useRef, useState } from "react";

// Straight from the handler, not the `@/services` barrel: the barrel pulls in the widget service,
// which imports the components, which import this hook — a cycle that left `StreamHandler`
// uninitialised and blanked the on-site build on a fresh load.
import { gamesListHandler } from "@/services/websocket/handlers/games-list.handler";

/** Names per request: a little over four screens of the dialog's 40px rows. */
const PAGE_SIZE = 50;
/** Typing pauses this long before the server is asked: one request per word, not per key. */
const SEARCH_DEBOUNCE_MS = 250;

type GamePagesState = {
  names: string[];
  /** Matches for the whole query, once the first page has answered. */
  total: number | null;
  loading: boolean;
  /** More pages exist beyond the loaded names. */
  hasMore: boolean;
};

const EMPTY: GamePagesState = {
  names: [],
  total: null,
  loading: false,
  hasMore: false,
};

/**
 * A campaign's eligible game names fetched a page at a time and searched on the server
 * (`action:list_games`) — for the games dialog of an "All games" campaign, whose names never ship
 * on the campaigns stream (the selection can be the whole catalogue, ~40k games). Pass `null` to
 * stay idle (the dialog is closed). A new query restarts from the first page; answers to an older
 * query are dropped, so fast typing can never show stale results.
 */
export const useCampaignGamePages = (
  campaignId: number | null,
  query: string
) => {
  const [state, setState] = useState<GamePagesState>(EMPTY);
  // Bumped on every new (campaign, query): a response from an older one is ignored.
  const generation = useRef(0);
  const inFlight = useRef(false);
  const nextOffset = useRef<number | null>(null);
  const trimmed = query.trim();

  const fetchPage = useCallback(
    (gen: number, offset: number) => {
      if (campaignId == null) return;
      inFlight.current = true;
      setState((s) => ({ ...s, loading: true }));
      gamesListHandler
        .listGames(campaignId, { query: trimmed, offset, limit: PAGE_SIZE })
        .then((page) => {
          if (gen !== generation.current) return;
          nextOffset.current = page.nextOffset;
          setState((s) => ({
            names: offset === 0 ? page.names : [...s.names, ...page.names],
            total: page.total,
            loading: false,
            hasMore: page.nextOffset != null,
          }));
        })
        // A failed page (dropped socket, timeout) leaves what is loaded; the dialog shows nothing
        // for an empty first page rather than claiming there are no games.
        .catch(() => {
          if (gen !== generation.current) return;
          setState((s) => ({ ...s, loading: false }));
        })
        .finally(() => {
          if (gen === generation.current) inFlight.current = false;
        });
    },
    [campaignId, trimmed]
  );

  useEffect(() => {
    const gen = ++generation.current;
    inFlight.current = false;
    nextOffset.current = null;
    if (campaignId == null) {
      setState(EMPTY);
      return;
    }
    setState({ ...EMPTY, loading: true });
    const timer = setTimeout(
      () => fetchPage(gen, 0),
      trimmed ? SEARCH_DEBOUNCE_MS : 0
    );
    return () => clearTimeout(timer);
  }, [campaignId, trimmed, fetchPage]);

  /** Fetch the next page (no-op while one is loading or when the last page is in). */
  const loadMore = useCallback(() => {
    if (inFlight.current || nextOffset.current == null) return;
    fetchPage(generation.current, nextOffset.current);
  }, [fetchPage]);

  return { ...state, loadMore };
};
